import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    IconButton,
} from "@mui/material";
import { CloudDownload } from "@mui/icons-material";
import { renderAsync as renderDocxAsync } from "docx-preview";
import caseNestDocumentService from "../services/caseNestDocumentService";
import { useTracking } from "react-tracking";
import trackingConstants from "../constants/trackingConstants";

// Renders a .docx from a blob/object URL using docx-preview.
const DocxPreview = ({ src, onError }) => {
    const containerRef = useRef(null);
    const styleRef = useRef(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch(src);
                const arrayBuffer = await response.arrayBuffer();
                if (cancelled || !containerRef.current) return;
                containerRef.current.innerHTML = "";
                if (styleRef.current) styleRef.current.innerHTML = "";
                await renderDocxAsync(
                    arrayBuffer,
                    containerRef.current,
                    styleRef.current ?? undefined,
                    { className: "docx", inWrapper: true, breakPages: true, experimental: true }
                );
            } catch (e) {
                console.error(e);
                if (!cancelled) onError?.(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [src]);

    return (
        <Box sx={{ maxHeight: "80vh", overflow: "auto", bgcolor: "#fff" }}>
            <div ref={styleRef} />
            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            )}
            <div ref={containerRef} />
        </Box>
    );
};

const renderPreview = (fileType, filePath, onError) => {
    const ext = (fileType || "").toLowerCase();
    if (ext === "pdf")
        return (
            <iframe
                src={filePath}
                title="Document preview"
                style={{ width: "100%", height: "80vh", border: "none" }}
            />
        );
    if (ext === "docx") return <DocxPreview src={filePath} onError={onError} />;
    return (
        <Typography variant="body2" sx={{ p: 2 }}>
            Preview is not available for this file type. Use the download button to open it.
        </Typography>
    );
};

const FileViewerModal = ({
    open,
    onClose,
    fileType,
    filePath,
    fileName,
    azureGuid,
    precedentId,
}) => {
    const { trackEvent } = useTracking();

    const downloadDocument = async () => {
        if (precedentId)
            trackEvent({
                event: `${trackingConstants.events.downloadPrecedent}: ${precedentId}`,
            });

        const response = await caseNestDocumentService.fetchDocument(azureGuid, "arraybuffer");
        const blob = new Blob([response], { type: fileType });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };

    return (
        <Dialog className="file-modal" onClose={onClose} open={open} fullWidth maxWidth="lg">
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Viewing document - {fileName}</Typography>
                    <IconButton onClick={downloadDocument}>
                        <CloudDownload />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>{renderPreview(fileType, filePath, () => {})}</DialogContent>
        </Dialog>
    );
};

export default FileViewerModal;
